/**
 * DCP delivery analytics — per-recipient lifecycle drawer.
 *
 * Mirrors the watermark analytics drawer's structure but scoped to
 * delivery facts (no NDA — DCP shares aren't gated by one — and no
 * watch sessions — DCPs aren't streamed). Lifts the
 * DeliveryLifecycleTimeline component out of WatermarkEncodeLibrary
 * so both products render the timeline identically.
 *
 * Opened from the DCP Library row's "Watch analytics" icon and from
 * the DCP Distribution per-recipient card.
 */
import React, { useEffect, useState } from 'react';
import {
  Box, Drawer, Typography, Alert, CircularProgress, Chip, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Tooltip, Collapse, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Autocomplete,
} from '@mui/material';
import {
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Undo as UndoIcon,
} from '@mui/icons-material';
import api from '../services/api';
import { fmtDateTime, fmtDateTimeSeconds } from '../utils/formatDate';
import { DeliveryLifecycleTimeline } from '../pages/WatermarkEncodeLibrary';
// Pure classifier extracted so it's import-testable. Keep this drawer
// focused on rendering; the lifecycle taxonomy lives in one place.
import { classifyDownload, fmtBytes } from '../utils/downloadStatus';

// fmtBytes is now imported from utils/downloadStatus.

function fmtDur(s) {
  if (s == null) return '—';
  if (s < 1) return `${(s * 1000).toFixed(0)}ms`;
  if (s < 60) return `${s.toFixed(1)}s`;
  return `${(s / 60).toFixed(1)}m`;
}
function fmtClock(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  const time = d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  if (sameDay) return `Today ${time}`;
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

// Common alternate-delivery methods — lets the operator pick from a
// list instead of typing free-form, but freeSolo so anything else
// works too (in-person handoff in a specific city, hand-off after a
// festival screening, etc.).
const DELIVERY_METHODS = [
  'Portable hard drive',
  'FileMail',
  'Cinesend',
  'Aspera',
  'WeTransfer',
  'Operator-direct download',
  'In-person hand-off',
  'Resent the link (recipient confirmed)',
];

function MarkDeliveredDialog({ open, onClose, share, onSaved }) {
  const [method, setMethod] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  useEffect(() => {
    if (!open) return;
    setMethod(''); setErr(null); setBusy(false);
  }, [open]);
  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      const { data } = await api.post(`/api/delivery/shares/${share.share_id}/mark-delivered`, { note: method || null });
      onSaved && onSaved(data);
      onClose();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || 'Failed to mark delivered');
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>Mark as delivered</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Confirm this recipient received <strong>{share?.recipient_name || share?.recipient_email}</strong>'s package outside of MinIO. The Delivered (manual) state will replace the current "no transfer recorded" status in analytics.
        </Typography>
        {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
        <Autocomplete
          freeSolo
          options={DELIVERY_METHODS}
          value={method}
          onChange={(_, v) => setMethod(v || '')}
          onInputChange={(_, v) => setMethod(v || '')}
          renderInput={(params) => (
            <TextField {...params} label="How did they get it?" size="small" placeholder="Drive / FileMail / etc." />
          )}
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
          Optional but recommended — this note shows on the recipient card and in the audit log.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" startIcon={<CheckCircleIcon />} onClick={submit} disabled={busy}>
          Mark as delivered
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function RecipientCard({ r, encrypted, onRefresh }) {
  const [expanded, setExpanded] = useState(false);
  const [markDialog, setMarkDialog] = useState(false);
  const tfs = r.transfers;

  const hasKdm = !!r.kdm;
  const kdmExpired = r.kdm?.valid_to && new Date(r.kdm.valid_to).getTime() < Date.now();
  const kdmExpiringSoon = !kdmExpired && r.kdm?.valid_to
    && (new Date(r.kdm.valid_to).getTime() - Date.now()) <= 48 * 36e5;

  const status = classifyDownload(r);

  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, mb: 1, bgcolor: 'background.paper' }}>
      <Box
        onClick={() => setExpanded((e) => !e)}
        sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.25, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
      >
        <IconButton size="small" sx={{ p: 0.25 }} tabIndex={-1}>
          {expanded ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
        </IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600} noWrap>
            {r.recipient_name || '—'}
          </Typography>
          {r.recipient_email && (
            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
              {r.recipient_email}
            </Typography>
          )}
        </Box>
        <Chip size="small" label={status.label} color={status.color} variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />
        {encrypted && (
          hasKdm
            ? <Chip size="small" label="✓ KDM" color="success" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
            : <Chip size="small" label="No KDM" color="warning" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
        )}
        {kdmExpired && <Chip size="small" label="EXPIRED" color="error" sx={{ height: 20, fontSize: '0.6rem', fontWeight: 700 }} />}
        {!kdmExpired && kdmExpiringSoon && (
          <Chip size="small" label="≤48h" color="warning" sx={{ height: 20, fontSize: '0.6rem' }} />
        )}
        <Tooltip title={
          <Box sx={{ p: 0.5, fontSize: '0.75rem', lineHeight: 1.5 }}>
            <div><strong>{r.view_count || 0} viewed</strong> — opened the share landing page</div>
            <div><strong>{r.public_download_count || 0} started</strong> — clicked Download</div>
            <div><strong>{r.confirmed_count || 0} complete</strong> — finished downloading (full file served, verified)</div>
          </Box>
        }>
          <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary', cursor: 'help' }}>
            {r.view_count || 0} v · {r.public_download_count || 0} s · {r.confirmed_count || 0} c
          </Typography>
        </Tooltip>
      </Box>

      <Collapse in={expanded} unmountOnExit timeout="auto">
        <Box sx={{ px: 2, pb: 1.5 }}>
          {/* Status banner — surfaces the load-bearing diagnosis ("stalled
              for X hours" / "in flight" / "delivered with M GB at N Mbps")
              up top so the operator doesn't have to read the table to
              know what state the delivery is in. */}
          {status.detail && (
            <Alert
              severity={status.color === 'success' ? 'success' : status.color === 'error' ? 'error' : status.color === 'warning' ? 'warning' : 'info'}
              sx={{ mb: 1.5, '& .MuiAlert-message': { fontSize: '0.78rem' } }}
              action={
                // Manual-mark / un-mark — closes the analytics gap when
                // the package reached the recipient outside MinIO.
                status.key === 'delivered_manual' ? (
                  <Tooltip title="Undo the manual confirmation">
                    <Button size="small" startIcon={<UndoIcon fontSize="small" />} onClick={async () => {
                      try {
                        await api.delete(`/api/delivery/shares/${r.share_id}/mark-delivered`);
                        onRefresh && onRefresh();
                      } catch (_) { /* fall through */ }
                    }}>Un-mark</Button>
                  </Tooltip>
                ) : (status.key === 'stalled' || status.key === 'incomplete') ? (
                  <Button size="small" variant="contained" color="success" startIcon={<CheckCircleIcon fontSize="small" />} onClick={() => setMarkDialog(true)}>
                    Mark as delivered
                  </Button>
                ) : null
              }
            >
              <Typography variant="caption" fontWeight={600} sx={{ display: 'block', mb: 0.25 }}>{status.label}</Typography>
              <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.45 }}>{status.detail}</Typography>
            </Alert>
          )}
          <MarkDeliveredDialog
            open={markDialog}
            onClose={() => setMarkDialog(false)}
            share={r}
            onSaved={() => { setMarkDialog(false); onRefresh && onRefresh(); }}
          />

          {/* Lifecycle timeline — same component the watermark drawer
              uses, with NDA disabled since DCP doesn't have one. */}
          <DeliveryLifecycleTimeline lifecycle={r.lifecycle} includeNda={false} fmtClock={fmtClock} />

          {/* KDM detail if present */}
          {r.kdm && (
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: 0.4, fontSize: '0.65rem', mb: 0.5 }}>
                Unlock Key
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', pl: 0.5 }}>
                <Chip size="small" label={r.kdm.status === 'completed' ? '✓ Issued' : `KDM ${r.kdm.status}`} color={r.kdm.status === 'completed' ? 'success' : 'default'} variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                {r.kdm.screen_name && <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>{r.kdm.screen_name}</Typography>}
                {r.kdm.valid_from && r.kdm.valid_to && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    valid {fmtDateTime(r.kdm.valid_from)} → {fmtDateTime(r.kdm.valid_to)}
                  </Typography>
                )}
                {kdmExpired && <Chip size="small" label="EXPIRED" color="error" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700 }} />}
                {!kdmExpired && kdmExpiringSoon && (
                  <Chip size="small" label="expiring ≤48h" color="warning" sx={{ height: 18, fontSize: '0.6rem' }} />
                )}
              </Box>
            </Box>
          )}

          {/* Transfer telemetry — same shape the Distribution per-venue
              card uses. Operator gets the same depth here. */}
          {tfs && tfs.count > 0 ? (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: 0.4, fontSize: '0.65rem', mb: 0.5 }}>
                Download transfers
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', fontSize: '0.7rem', color: 'text.secondary', mb: 0.5 }}>
                {tfs.count} transfer{tfs.count === 1 ? '' : 's'} · {tfs.complete} complete · {fmtBytes(tfs.total_bytes)} total · peak {tfs.peak_mbps.toFixed(0)} Mbps · last {tfs.last_completed_at ? fmtDateTime(tfs.last_completed_at) : '—'}
              </Typography>
              <Box sx={{ maxHeight: 240, overflow: 'auto' }}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ '& th': { py: 0.25, fontSize: '0.65rem', textTransform: 'none', letterSpacing: 0 } }}>
                        <TableCell>Started</TableCell>
                        <TableCell>Finished</TableCell>
                        <TableCell align="right">Size</TableCell>
                        <TableCell align="right">%</TableCell>
                        <TableCell align="right">Mbps</TableCell>
                        <TableCell align="right">Duration</TableCell>
                        <TableCell>HTTP</TableCell>
                        <TableCell>IP</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tfs.rows.map((t) => {
                        const pct = t.expected_size && t.bytes_sent != null
                          ? Math.min(100, (t.bytes_sent / t.expected_size) * 100)
                          : null;
                        const started = t.started_at
                          ? t.started_at
                          : (t.completed_at && t.duration_seconds != null
                            ? new Date(new Date(t.completed_at).getTime() - t.duration_seconds * 1000).toISOString()
                            : null);
                        return (
                          <TableRow key={t.id} sx={{ '& td': { py: 0.25, fontSize: '0.65rem' } }}>
                            <TableCell>{started ? fmtDateTimeSeconds(started) : '—'}</TableCell>
                            <TableCell>{t.completed_at ? fmtDateTimeSeconds(t.completed_at) : '—'}</TableCell>
                            <TableCell align="right">{fmtBytes(t.bytes_sent)}</TableCell>
                            <TableCell align="right">{pct == null ? '—' : `${pct.toFixed(1)}%`}{t.is_complete ? ' ✓' : ''}</TableCell>
                            <TableCell align="right">{t.avg_mbps != null ? t.avg_mbps.toFixed(1) : '—'}</TableCell>
                            <TableCell align="right">{fmtDur(t.duration_seconds)}</TableCell>
                            <TableCell>
                              <Chip size="small" variant="outlined" sx={{ fontSize: '0.6rem', height: 16 }}
                                label={t.http_status || '—'}
                                color={t.http_status >= 200 && t.http_status < 300 ? 'success' : t.http_status >= 400 ? 'error' : 'default'}
                              />
                            </TableCell>
                            <TableCell sx={{ fontFamily: 'monospace' }}>{t.client_ip || '—'}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Box>
          ) : null /* Status banner above already explains why there are no transfer rows. */}
        </Box>
      </Collapse>
    </Box>
  );
}

export default function DCPDeliveryAnalyticsDrawer({ open, dcp, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const reload = React.useCallback(() => {
    if (!dcp?.id) return;
    setLoading(true);
    setError(null);
    api.get(`/api/dcp-jobs/${dcp.id}/delivery-analytics`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, [dcp?.id]);

  useEffect(() => {
    if (!open || !dcp?.id) return undefined;
    setData(null);
    reload();
  }, [open, dcp?.id, reload]);

  return (
    <Drawer anchor="right" open={open} onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100vw', md: 880 }, p: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6">Delivery analytics</Typography>
          <Typography variant="caption" color="text.secondary">
            {dcp?.name || `DCP #${dcp?.id || ''}`}{dcp?.encrypt ? ' 🔐 encrypted' : ''}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </Box>

      {loading && <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Box>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {data && (
        <>
          <Typography variant="body1" sx={{ fontWeight: 500, mb: 2 }}>{data.headline}</Typography>
          {data.summary && (
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <Stat label="Total recipients"   value={data.summary.total_recipients} />
              <Stat label="Sent"                value={data.summary.sent} />
              <Stat label="Opened"              value={data.summary.opened} />
              <Stat label="Downloads started"   value={data.summary.download_started} />
              <Stat label="Downloads complete"  value={data.summary.download_complete} accent={data.summary.download_complete > 0 ? 'success' : undefined} />
            </Box>
          )}
          <Divider sx={{ mb: 2 }} />
          {(data.recipients || []).length === 0 ? (
            <Typography color="text.secondary">No deliveries yet.</Typography>
          ) : (
            <Box>
              {data.recipients.map((r) => (
                <RecipientCard key={r.share_id} r={r} encrypted={data.summary?.encrypted} onRefresh={reload} />
              ))}
            </Box>
          )}
        </>
      )}
    </Drawer>
  );
}

function Stat({ label, value, accent }) {
  const colorMap = { success: 'success.main', warning: 'warning.main', error: 'error.main', info: 'info.main' };
  return (
    <Box sx={{ minWidth: 110 }}>
      <Typography variant="h5" fontWeight={700} sx={{ color: colorMap[accent] || 'text.primary' }}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>{label}</Typography>
    </Box>
  );
}
